import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import {
  PARTNER_PROGRAM_QUERY,
  INQUIRY_QUERY,
  CUSTOMER_QUERY,
  ASSET_CONTENT_QUERY,
  USER_QUERY,
  LOGISTIC_CENTER_QUERY,
  ORDER_QUERY,
  BRAND_QUERY,
  BRAND_LINE_QUERY,
  COUNTRY_QUERY
} from './queries'
import { RouteType } from './types'
import detectRouteType from './useDetectRouteType'

/**
 * Custom hook to fetch entity names for breadcrumb display
 * @param {string} path - Current URL path
 * @param {Object} params - Route params from useParams()
 * @returns {Object} crumbs - Object containing resolved entity names
 */
const useBreadcrumbQueries = (path, params) => {
  const routeType = detectRouteType(path)

  // Partner Program query
  const { data: partnerProgramData } = useQuery(PARTNER_PROGRAM_QUERY, {
    skip: !params?.id || routeType !== RouteType.PARTNER_PROGRAM,
    variables: { id: params?.id }
  })

  // Asset Content query
  const { data: assetContentData } = useQuery(ASSET_CONTENT_QUERY, {
    skip: !params?.id || routeType !== RouteType.ASSET_CONTENT,
    variables: { id: params?.id }
  })

  // Customer query
  const { data: customerData } = useQuery(CUSTOMER_QUERY, {
    skip: !params?.id || routeType !== RouteType.CUSTOMER,
    variables: { id: params?.id }
  })

  // User query (sales-reps and user-management)
  const { data: userData } = useQuery(USER_QUERY, {
    skip: !params?.id || routeType !== RouteType.USER,
    variables: { id: params?.id }
  })

  // Logistic Center query
  const { data: logisticCenterData } = useQuery(LOGISTIC_CENTER_QUERY, {
    skip: !params?.id || routeType !== RouteType.LOGISTIC_CENTER,
    variables: { id: params?.id }
  })

  // Order query
  const { data: orderData } = useQuery(ORDER_QUERY, {
    skip: !params?.id || routeType !== RouteType.ORDER,
    variables: { id: params?.id }
  })

  // Brand query
  const { data: brandData } = useQuery(BRAND_QUERY, {
    skip: !params?.id || routeType !== RouteType.BRAND,
    variables: { id: params?.id }
  })

  // BrandLine query
  const { data: brandLineData } = useQuery(BRAND_LINE_QUERY, {
    skip: !params?.id || routeType !== RouteType.BRAND_LINE,
    variables: { id: params?.id }
  })

  // Country query
  const { data: countryData } = useQuery(COUNTRY_QUERY, {
    skip: !params?.id || routeType !== RouteType.COUNTRY,
    variables: { id: params?.id }
  })

  // Inquiry query (nested param)
  const { data: inquiryData } = useQuery(INQUIRY_QUERY, {
    skip: !params?.inquiryId,
    variables: { id: params?.inquiryId }
  })

  // Customer query (for customerId param in nested routes)
  const { data: nestedCustomerData } = useQuery(CUSTOMER_QUERY, {
    skip: !params?.customerId,
    variables: { id: params?.customerId }
  })

  // Derive crumbs from query data
  const crumbs = useMemo(() => {
    const result = {}

    // Resolve id based on route type
    if (partnerProgramData?.partnerProgram?.name) {
      result.id = partnerProgramData.partnerProgram.name
    } else if (assetContentData?.assetContent?.title) {
      result.id = assetContentData.assetContent.title
    } else if (customerData?.customer?.name) {
      result.id = customerData.customer.name
    } else if (userData?.user?.name) {
      result.id = userData.user.name
    } else if (logisticCenterData?.logisticCenter?.name) {
      result.id = logisticCenterData.logisticCenter.name
    } else if (orderData?.order?.id) {
      result.id = `Order #${orderData.order.id}`
    } else if (brandData?.brand?.name) {
      result.id = brandData.brand.name
    } else if (brandLineData?.brandLine?.name) {
      result.id = brandLineData.brandLine.name
    } else if (countryData?.country?.name) {
      result.id = countryData.country.name
    }

    // Nested params
    if (inquiryData?.inquiry?.name) {
      result.inquiryId = inquiryData.inquiry.name
    }
    if (nestedCustomerData?.customer?.name) {
      result.customerId = nestedCustomerData.customer.name
    }

    return result
  }, [
    partnerProgramData,
    assetContentData,
    customerData,
    userData,
    logisticCenterData,
    orderData,
    brandData,
    brandLineData,
    countryData,
    inquiryData,
    nestedCustomerData
  ])

  return crumbs
}

export default useBreadcrumbQueries
